import React, { useEffect, useState, useCallback } from "react";
import { Package, LinkSimple, DownloadSimple, CheckCircle, Circle } from "@phosphor-icons/react";
import { authClient } from "../lib/authApi";
import { downloadPackageZip } from "../lib/api";
import { errorMessage } from "../lib/errorMessage";
import { toast } from "./ui/sonner";
import { useTranslation } from "react-i18next";
import { isSignedOff } from "../workflow/calcStatus";   // A2-581 lead 35

// v0.6 — Filing package panel (Option 2). Links a UK_VAT + an EU_OSS calc
// prepared from the SAME dataset so a GB+EU seller files both returns and the
// UK calc's out-of-scope OSS/ICS rows auto-resolve against the EU_OSS sibling.
const ROLE_LABEL = { UK_VAT: "UK VAT (VAT100)", EU_OSS: "EU One-Stop-Shop" };

const MemberSlot = ({ role, member }) => {
  const { t } = useTranslation();
  const filled = !!member;
  return (
    <div
      data-testid={`package-slot-${role}`}
      className="flex items-center justify-between px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-sm"
    >
      <div className="flex items-center gap-2 text-[11px]">
        {filled
          ? <CheckCircle size={14} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
          : <Circle size={14} className="text-slate-400" />}
        <span className="text-slate-700 dark:text-slate-200">{ROLE_LABEL[role] || role}</span>
      </div>
      <span className="text-[11px] text-slate-500 dark:text-slate-400">
        {filled
          ? (member.frozen ? t("ui.filing_package.status_frozen", { defaultValue: "frozen" }) : (isSignedOff(member) ? t("ui.filing_package.status_signed", { defaultValue: "signed off" }) : t("ui.filing_package.status_draft", { defaultValue: "draft" })))
          : t("ui.filing_package.status_unlinked", { defaultValue: "not linked" })}
      </span>
    </div>
  );
};

export const FilingPackagePanel = ({ calcId }) => {
  const { t } = useTranslation();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!calcId) return;
    try {
      const { data } = await authClient.get(`/runs/calc/${calcId}/package`);
      setPkg(data.package || null);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [calcId]);

  useEffect(() => { setLoading(true); refresh(); }, [refresh]);

  const attach = async () => {
    setBusy(true);
    try {
      const { data } = await authClient.post(`/filing-packages`, { calc_id: calcId });
      setPkg(data);
      toast.success("Calc added to a filing package.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadBoth = async () => {
    if (!pkg?.id) return;
    setBusy(true);
    const r = await downloadPackageZip(pkg.id);
    setBusy(false);
    if (!r.ok) toast.error(r.error || "Download failed");
  };

  if (loading || !calcId) return null;

  const members = pkg?.members || {};
  const bothPresent = !!(members.UK_VAT && members.EU_OSS);

  return (
    <section
      data-testid="filing-package-panel"
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-sm"
    >
      <header className="border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <Package size={14} /> {t("ui.filing_package.title", { defaultValue: "Filing package" })}
      </header>

      {!pkg ? (
        <div className="px-4 py-3 space-y-2">
          <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed">
            {t("ui.filing_package.intro", { defaultValue: "Filing both a UK VAT100 and an EU OSS return from this dataset? Link them into one package so the UK calc's out-of-scope OSS/ICS rows auto-resolve against their EU OSS sibling — no per-row acknowledgement needed." })}
          </p>
          <button
            type="button"
            data-testid="package-attach-btn"
            onClick={attach}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-950/40 text-teal-800 dark:text-teal-200 text-[11px] hover:bg-teal-100 dark:hover:bg-teal-900/40 disabled:opacity-50"
          >
            <LinkSimple size={12} weight="bold" /> {t("ui.filing_package.add", { defaultValue: "Add this calc to a filing package" })}
          </button>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-3">
          <div className="grid grid-cols-1 gap-2">
            <MemberSlot role="UK_VAT" member={members.UK_VAT} />
            <MemberSlot role="EU_OSS" member={members.EU_OSS} />
          </div>
          {!bothPresent && (
            <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="package-incomplete-hint">
              {t("ui.filing_package.incomplete", { defaultValue: "Link the sibling calc (the other regime over the same dataset) from its own Step 6 to complete the package." })}
            </p>
          )}
          <button
            type="button"
            data-testid="package-download-both-btn"
            onClick={downloadBoth}
            disabled={busy || !bothPresent}
            title={bothPresent ? "" : t("ui.filing_package.both_first", { defaultValue: "Both returns must be linked first" })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-[11px] hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            <DownloadSimple size={12} weight="bold" /> {t("ui.filing_package.download_both", { defaultValue: "Download both returns (.zip)" })}
          </button>
          <p className="text-[10px] text-slate-400 leading-relaxed">
            {t("ui.filing_package.separate", { defaultValue: "The two workbooks file to different authorities (HMRC vs the OSS portal) — submit each to its own portal, don't merge them." })}
          </p>
        </div>
      )}
    </section>
  );
};
